import { getProductModifier } from '@/app/[locale]/_service/product.service'
import type { IModifierQuantityObj } from '@/app/[locale]/_store/types'
import type {
  IGroupModifier,
  IProduct,
  IProductModifiers,
  ISavedModifier,
} from '@/app/[locale]/_types'

export interface ProductModifierState {
  modifiers: ISavedModifier[]
  groupAmounts: IModifierQuantityObj[]
  compulsoryModifiers: ISavedModifier[]
}

export const MODIFIERS_MIN_HEIGHT = '246px'

export function isGroupVisible(
  group: Pick<IGroupModifier, 'is_visible'>
): boolean {
  return group?.is_visible !== false
}

export function shouldOpenModifierModal(
  pm: IProductModifiers | undefined
): boolean {
  if (!pm) return true

  const hasSingles = (pm.single_modifiers?.length ?? 0) > 0
  const hasVisibleGroup =
    pm.group_modifiers?.some((group) => isGroupVisible(group)) ?? false

  return hasSingles || hasVisibleGroup
}

export function hasInvisibleGroupModifier(
  pm: IProductModifiers | undefined
): boolean {
  return pm?.group_modifiers?.some((group) => group.is_visible === false) ?? false
}

export function getVisibleGroupModifiers(
  pm: IProductModifiers | undefined
): IGroupModifier[] {
  return pm?.group_modifiers?.filter(isGroupVisible) ?? []
}

function checkIsActive(activeInMenu: boolean, menuId: string | undefined) {
  return (menuId && activeInMenu) || !menuId
}

function isGroupCompulsory(group: IGroupModifier): boolean {
  return Boolean(group.is_compulsory)
}

/** Builds default modifier selections, group totals, and compulsory list from API data. */
export function buildProductModifierState(
  productModifiers: IProductModifiers | undefined,
  menuId?: string
): ProductModifierState {
  const modifiers: ISavedModifier[] = []
  const groupAmounts: IModifierQuantityObj[] = []
  const compulsoryModifiers: ISavedModifier[] = []

  if (!productModifiers) {
    return { modifiers, groupAmounts, compulsoryModifiers }
  }

  productModifiers.group_modifiers?.forEach((group) => {
    const isGroupVis = isGroupVisible(group)
    let amount = 0

    group.variants?.forEach((variant) => {
      const isActiveVariant = isGroupVis
        ? checkIsActive(
            group.active_in_menu && variant.active_in_menu,
            menuId
          )
        : checkIsActive(variant.active_in_menu, menuId)

      const defaultQuantity = isGroupVis
        ? variant.default_amount || variant.min_amount || 0
        : variant.default_amount || 0

      const item: ISavedModifier = {
        name: variant.title,
        parent_id: group.id,
        parent_name: group.name,
        parent_min_amount: group.min_amount,
        modifier_id: variant.id,
        modifier_price: +variant.out_price,
        max_amount: variant.max_amount || 1,
        min_amount: variant.min_amount || 0,
        modifier_quantity: isActiveVariant ? defaultQuantity : 0,
        type: 'group',
        is_group_checkbox: group.is_checkbox,
      }

      modifiers.push(item)

      if (isActiveVariant) {
        amount += defaultQuantity
      }

      if (isGroupCompulsory(group) && isGroupVis) {
        compulsoryModifiers.push(item)
      }
    })

    groupAmounts.push({ id: group.id, quantity: amount })
  })

  productModifiers.single_modifiers?.forEach((item) => {
    const isActive = checkIsActive(item.active_in_menu, menuId)

    const singleModifier: ISavedModifier = {
      modifier_id: item.id,
      modifier_price: +item.price,
      max_amount: item.max_amount || 1,
      min_amount: item.min_amount || 0,
      modifier_quantity: isActive ? item.min_amount || 0 : 0,
      parent_id: '',
      name: item.name,
      type: 'single',
    }

    modifiers.push(singleModifier)

    if (item.is_compulsory) {
      compulsoryModifiers.push(singleModifier)
    }
  })

  return { modifiers, groupAmounts, compulsoryModifiers }
}

/** Returns only the compulsory modifier entries from API product modifier data. */
export function buildCompulsoryModifiers(
  productModifiers: IProductModifiers | undefined,
  menuId?: string
): ISavedModifier[] {
  return buildProductModifierState(productModifiers, menuId).compulsoryModifiers
}

/** Returns order modifiers with invisible group defaults applied. */
export function buildOrderModifiersFromProductModifiers(
  productModifiers: IProductModifiers | undefined,
  options?: { menuId?: string }
): ISavedModifier[] {
  return buildProductModifierState(productModifiers, options?.menuId).modifiers.filter(
    (modifier) => modifier.modifier_quantity > 0
  )
}

export function getActiveModifiersSignature(
  modifiers: ISavedModifier[] = []
): string {
  return modifiers
    .filter((modifier) => modifier.modifier_quantity > 0)
    .map((modifier) => `${modifier.modifier_id}:${modifier.modifier_quantity}`)
    .sort()
    .join('|')
}

export type OrderingDeliveryType = 'hall' | 'self-pickup' | 'delivery'

export function getOrderingDeliveryType({
  isHall,
  orderType,
}: {
  isHall: boolean
  orderType?: string
}): OrderingDeliveryType {
  if (isHall || orderType === 'hall') return 'hall'
  if (orderType === 'self-pickup') return 'self-pickup'
  return 'delivery'
}

export function buildProductModifierRequestParams({
  productId,
  menuId,
  deliveryType,
}: {
  productId: string
  menuId: string
  deliveryType?: string
}) {
  return {
    product_id: productId,
    menu_id: menuId,
    ...(deliveryType ? { delivery_type: deliveryType } : {}),
  }
}

/** Fetches modifiers and returns pre-filled order lines when the customize UI can be skipped. */
export async function fetchDirectCartModifiers(
  productId: string,
  menuId: string,
  deliveryType?: string
): Promise<ISavedModifier[] | null> {
  const response = await getProductModifier(
    buildProductModifierRequestParams({
      productId,
      menuId,
      deliveryType,
    })
  )
  const productModifiers = response?.data?.product_modifiers

  if (!productModifiers || shouldOpenModifierModal(productModifiers)) {
    return null
  }

  const orderModifiers = buildOrderModifiersFromProductModifiers(productModifiers, {
    menuId: menuId || undefined,
  })

  return orderModifiers.length > 0 ? orderModifiers : null
}

export function getSelectedMenuId({
  isHall,
  pathnameEndsWithMenu,
  menuId,
  branchMenuId,
}: {
  isHall: boolean
  pathnameEndsWithMenu: boolean
  menuId: string
  branchMenuId?: string | null
}): string {
  if (isHall || pathnameEndsWithMenu) {
    return menuId !== 'null' ? menuId : ''
  }

  return branchMenuId || ''
}

export function canAddSimpleProductDirectly(
  product: Pick<IProduct, 'type' | 'has_modifier' | 'active_in_menu'>,
  menuId: string
): boolean {
  return (
    product.type === 'simple' &&
    !product.has_modifier &&
    (menuId ? Boolean(product.active_in_menu) : true)
  )
}

export function isOrderingLocationReady({
  isHall,
  orderType,
  branchId,
  geolocation,
}: {
  isHall: boolean
  orderType?: string
  branchId?: string | null
  geolocation?: string
}): boolean {
  return (
    isHall ||
    (orderType === 'self-pickup' && Boolean(branchId)) ||
    (orderType === 'delivery' && geolocation === 'accepted')
  )
}
